import { Box, Button, CircularProgress, HStack, Stack, Text, VStack, useToast } from "@chakra-ui/react";
import { parse } from "date-fns";
import { object, string } from "decoders";
import { useCallback } from "react";
import { AiFillPrinter } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { P, match } from "ts-pattern";
import { useCreateWellLevelsPrintData, useWellLevelsData } from "../../services/projectService";
import { mapSitesFromWellLevelsData } from "../../types/map";
import { WellLevelsData, getWellLevelsSitesStats } from "../../types/wellLevelsData";
import { useWellLevelsForm } from "../../types/wellLevelsForm";
import { DashboardMapView } from "../DashboardMapView/DashboardMapView";
import { WellLevelsForm } from "../Form/WellLevelsForm/WellLevelsForm";
import { FormBgContainer } from "../FormBgContainer/FormBgContainer";
import { PrintGraphsButton } from "../PrintGraphsButton/PrintGraphsButton";
import { SitesStatistics } from "../SitesStatistics/SitesStatistics";
import { TabHeading } from "../TabHeading/TabHeading";
import { ScadaWellLevelsGraph } from "./ScadaWellLevelsGraph/ScadaWellLevelsGraph";
import { WellLevelsGraph } from "./WellLevelsGraph/WellLevelsGraph";
import { WellLevelNotes } from "./WellLevelNotes/WellLevelNotes";

export function WellLevelsView() {
  const { formik, formValues } = useWellLevelsForm();
  const { sites, startDate, endDate } = formValues;

  return (
    <VStack alignItems={"stretch"}>
      <FormBgContainer>
        <VStack alignItems={"stretch"} flex="1">
          <TabHeading text="Groundwater Well Levels" />
          <WellLevelsForm formik={formik} />
        </VStack>
      </FormBgContainer>

      {startDate && endDate && (
        <Box padding={16} paddingTop={0}>
          <WellLevelsDashboard
            sites={sites}
            startDate={parse(startDate, "yyyy-MM-dd", new Date())}
            endDate={parse(endDate, "yyyy-MM-dd", new Date())}
          />
        </Box>
      )}
    </VStack>
  );
}

function WellLevelsDashboard({ sites, startDate, endDate }: { sites: string[]; startDate: Date; endDate: Date }) {
  const query = useWellLevelsData(startDate, endDate, sites);
  const printMutation = usePrintMutation(startDate, endDate, sites);

  return match(query)
    .with({ isLoading: true }, () => <CircularProgress isIndeterminate color="brand.400" />)
    .with({ isError: true }, () => <Text color="red">Error loading well levels data</Text>)
    .with({ data: P.nullish }, () => <></>)
    .otherwise(({ data }) => (
      <VStack alignItems={"stretch"} gap={8}>
        <HStack justifyContent={"space-between"}>
          <Button
            isLoading={printMutation.isLoading}
            onClick={() => printMutation.mutate()}
            leftIcon={<AiFillPrinter />}
          >
            Print Data
          </Button>
          <PrintGraphsButton />
        </HStack>
        <WellLevelsDashboardContent data={data} sites={sites} />
      </VStack>
    ));
}

function usePrintMutation(startDate: Date, endDate: Date, sites: string[]) {
  const mutation = useCreateWellLevelsPrintData(startDate, endDate, sites);
  const navigate = useNavigate();
  const toast = useToast();

  const mutate = useCallback(
    () =>
      mutation.mutate(undefined, {
        onError: () => toast({ title: "An error ocurred, please try again later", status: "error" }),
        onSuccess: (data) => {
          const decoded = object({ printId: string }).value(data);

          if (decoded) {
            navigate(`/print/${decoded.printId}`);
          } else {
            toast({ title: "Failed to retreive print version", status: "error" });
          }
        },
      }),
    [mutation, navigate, toast],
  );

  return { ...mutation, mutate };
}

function WellLevelsDashboardContent({ data, sites }: { data: WellLevelsData; sites: string[] }) {
  const stats = getWellLevelsSitesStats(sites, data);
  return (
    <VStack alignItems={"stretch"} gap={4}>
      <WellLevelsGraph wellLevelsData={data} sites={sites} />
      <ScadaWellLevelsGraph wellLevelData={data.scadaRecords} sites={sites} />
      <WellLevelNotes wellLevelNotes={data.wellLevelNotes} />
      <Stack direction={{ base: "column", md: "row" }} alignItems={"stretch"}>
        <SitesStatistics
          unit=""
          sitesStats={getWellLevelsSitesStats(sites, data)}
          title="Well Depth Statistics"
          colorContext="wellLevels"
        />
        <Box flex={1} minWidth={"600px"}>
          <DashboardMapView
            mapSites={mapSitesFromWellLevelsData(data.coordinates, stats)}
            colorContext="wellLevels"
          ></DashboardMapView>
        </Box>
      </Stack>
    </VStack>
  );
}
